

import { InputBlob } from "@/components/Inputs/InputBlob";
import { FormInput } from "@/components/Inputs/FormInput";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { TertiaryButton } from "@/components/Buttons/TertiaryButton";
import { Spacing } from "@/components/Spacing/Spacing";
import { useTranslations } from "next-intl";
import { useMfaVerifyForm } from "./useMfaVerifyForm";

const MAX_ATTEMPTS = 5;

const subtitleKey = {
  totp: "subtitle",
  recovery: "subtitleRecovery",
  email: "subtitleEmail",
} as const;

export default function MfaVerifyContent({
  initialTimeRemaining,
}: {
  initialTimeRemaining: number;
}) {
  const t = useTranslations("mfaVerify");

  const {
    otp,
    recoveryCode,
    mode,
    error,
    isLoading,
    isEmailSending,
    emailSent,
    activeInput,
    timeRemaining,
    sessionWarning,
    attempts,
    emailCooldown,
    handleChange,
    handleKeyDown,
    handlePaste,
    handleModeSwitch,
    handleSendEmail,
    handleSubmit,
    handleCancel,
    updateState,
  } = useMfaVerifyForm(initialTimeRemaining);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col items-center w-full">
      {/* Session warning banner */}
      {sessionWarning && timeRemaining > 0 && (
        <div className="w-full p-2 mb-2 rounded-lg surface-warning text-center">
          <p className="body-2-regular font-warning">
            {t("sessionWarning", { seconds: timeRemaining })}
          </p>
        </div>
      )}

      <Spacing height="h-4" />
      <p className="body-2-regular font-secondary text-center">
        {t(subtitleKey[mode])}
      </p>
      <Spacing height="h-8" />

      {/* OTP input for TOTP and Email modes */}
      {(mode === "totp" || mode === "email") && (
        <div className="flex space-x-2">
          {otp.map((digit, index) => (
            <InputBlob
              key={index}
              index={index}
              value={digit}
              isActive={index === activeInput}
              changAction={handleChange}
              keyDownAction={handleKeyDown}
              onPaste={index === 0 ? handlePaste : undefined}
            />
          ))}
        </div>
      )}

      {/* Recovery code input */}
      {mode === "recovery" && (
        <div className="w-full">
          <FormInput
            id="recovery-code"
            type="text"
            value={recoveryCode}
            label={t("recoveryCodePlaceholder")}
            onChangeAction={(value) => updateState({ recoveryCode: value, error: "" })}
          />
        </div>
      )}

      {/* Email sent confirmation */}
      {mode === "email" && emailSent && (
        <p className="body-2-regular font-success mt-2">{t("emailSent")}</p>
      )}

      {/* Error display */}
      {error && (
        <p role="alert" data-testid="mfa-verify-error" className="font-error body-2-regular mt-2">
          {error}
        </p>
      )}

      {/* Attempts remaining */}
      {attempts >= 2 && attempts < MAX_ATTEMPTS && (
        <p className="body-2-regular font-warning mt-1">
          {t("attemptsRemaining", { count: MAX_ATTEMPTS - attempts })}
        </p>
      )}

      <Spacing height="h-6" />

      {/* Submit button */}
      <PrimaryButton
        type="submit"
        size="small"
        text={t("submit")}
        disabled={isLoading || attempts >= MAX_ATTEMPTS || timeRemaining === 0}
        loading={isLoading}
      />

      <Spacing height="h-3" />

      {/* Mode switchers */}
      {mode === "totp" && (
        <>
          <TertiaryButton
            size="small"
            text={t("useRecoveryCode")}
            onClick={() => handleModeSwitch("recovery")}
          />
          <Spacing height="h-2" />
          <TertiaryButton
            size="small"
            text={t("sendEmailCode")}
            onClick={() => {
              handleModeSwitch("email");
              handleSendEmail();
            }}
          />
        </>
      )}

      {mode === "recovery" && (
        <TertiaryButton
          size="small"
          text={t("useAppCode")}
          onClick={() => handleModeSwitch("totp")}
        />
      )}

      {mode === "email" && (
        <>
          <TertiaryButton
            size="small"
            text={t("useAppCode")}
            onClick={() => handleModeSwitch("totp")}
          />
          <Spacing height="h-2" />
          <TertiaryButton
            size="small"
            text={
              isEmailSending
                ? t("loading")
                : emailCooldown > 0
                  ? `${t("resendEmail")} (${emailCooldown})`
                  : t("resendEmail")
            }
            onClick={isEmailSending || emailCooldown > 0 ? () => {} : handleSendEmail}
            disabled={isEmailSending || emailCooldown > 0}
          />
        </>
      )}

      <Spacing height="h-4" />
      <TertiaryButton size="small" text={t("backToLogin")} onClick={handleCancel} />
    </form>
  );
}
